import type { StudentDto, StudentsListQuery } from '@mm/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listStudents } from '../api/students';
import { OnlineDot } from '../components/OnlineDot';
import { useOnlineStudentsSet } from '../hooks/useStudentPresence';
import { AppLayout } from '../layout/AppLayout';
import { Button } from '../ui/Button';
import { IconArrowLeft, IconArrowRight, IconPlus, IconSearch } from '../ui/icons';
import { Input } from '../ui/Input';
import { Table, TBody, TD, TH, THead, TR } from '../ui/Table';

function parseQuery(sp: URLSearchParams): Partial<StudentsListQuery> {
  return {
    q: sp.get('q') ?? undefined,
    sort: (sp.get('sort') as StudentsListQuery['sort']) ?? 'fullName',
    order: (sp.get('order') as StudentsListQuery['order']) ?? 'asc',
    page: sp.get('page') ? Number(sp.get('page')) : 1,
    pageSize: 20,
  };
}

export function StudentsListPage() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState<StudentDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const q = parseQuery(sp);
  const online = useOnlineStudentsSet();

  const load = useCallback(async () => {
    setLoading(true);
    const r = await listStudents(q);
    setItems(r.items);
    setTotal(r.total);
    setLoading(false);
  }, [sp]);

  useEffect(() => {
    load();
  }, [load]);

  function updateSp(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSp(next);
  }

  function toggleSort(col: NonNullable<StudentsListQuery['sort']>) {
    const nextOrder = q.sort === col ? (q.order === 'asc' ? 'desc' : 'asc') : 'asc';
    updateSp({ sort: col, order: nextOrder });
  }

  const pages = Math.max(1, Math.ceil(total / (q.pageSize ?? 20)));

  return (
    <AppLayout crumbs={[{ label: 'Админ' }, { label: 'Ученики' }]}>
      <div className="max-w-6xl px-8 py-7">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="font-display text-[38px] leading-none font-medium" style={{ fontVariationSettings: '"opsz" 96, "SOFT" 5' }}>
            Ученики
            {total > 0 ? (
              <span className="text-muted font-light text-[20px] ml-1.5" style={{ fontVariationSettings: '"opsz" 48' }}>
                — {total}
              </span>
            ) : null}
          </h1>
          <Link to="/app/students/new">
            <Button>
              <IconPlus size={12} />
              Новый ученик
            </Button>
          </Link>
        </div>
        <p className="font-caps italic text-[13px] text-muted mb-6">База активных подопечных</p>

        <div className="flex gap-2 mb-4">
          <div className="relative max-w-[340px] flex-1">
            <IconSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              className="pl-8"
              placeholder="Поиск по имени или email…"
              value={q.q ?? ''}
              onChange={(e) => updateSp({ q: e.target.value, page: '1' })}
            />
          </div>
        </div>

        <div className="rounded border border-border bg-bg overflow-hidden">
          <Table>
            <THead>
              <TR>
                <TH>
                  <button
                    type="button"
                    onClick={() => toggleSort('fullName')}
                    className="flex items-center gap-1 hover:text-fg transition-colors"
                  >
                    ФИО
                    {q.sort === 'fullName' ? (
                      <span className="text-accent">{q.order === 'asc' ? '↑' : '↓'}</span>
                    ) : null}
                  </button>
                </TH>
                <TH>Класс</TH>
                <TH>Email</TH>
                <TH>Телефон</TH>
                <TH>
                  <button
                    type="button"
                    onClick={() => toggleSort('updatedAt')}
                    className="flex items-center gap-1 hover:text-fg transition-colors"
                  >
                    Обновлено
                    {q.sort === 'updatedAt' ? (
                      <span className="text-accent">{q.order === 'asc' ? '↑' : '↓'}</span>
                    ) : null}
                  </button>
                </TH>
                <TH className="w-10" />
              </TR>
            </THead>
            <TBody>
              {loading ? (
                <TR className="hover:bg-transparent">
                  <TD colSpan={6} className="text-center text-muted py-8">
                    Загрузка…
                  </TD>
                </TR>
              ) : items.length === 0 ? (
                <TR className="hover:bg-transparent">
                  <TD colSpan={6} className="py-16">
                    <div className="flex flex-col items-center gap-3 text-muted">
                      <span className="font-display text-[72px] leading-none text-border" style={{ fontVariationSettings: '"opsz" 144' }}>
                        ∅
                      </span>
                      <p className="text-[13px]">
                        У вас пока нет учеников.{' '}
                        <Link to="/app/students/new" className="text-accent underline-offset-4 hover:underline">
                          Добавьте первого
                        </Link>
                        .
                      </p>
                    </div>
                  </TD>
                </TR>
              ) : (
                items.map((s) => (
                  <TR key={s.id}>
                    <TD>
                      <Link to={`/app/students/${s.id}`} className="font-medium text-fg hover:text-accent">
                        {s.fullName}
                      </Link>
                      <OnlineDot online={online.has(s.id)} className="ml-2 align-middle" />
                    </TD>
                    <TD className="font-serif-num text-muted">{s.grade ?? '—'}</TD>
                    <TD>
                      {s.email ? (
                        <span className="text-link">{s.email}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </TD>
                    <TD className="nums text-muted">{s.phone ?? '—'}</TD>
                    <TD className="nums text-muted">
                      {new Date(s.updatedAt).toLocaleDateString('ru')}
                    </TD>
                    <TD className="text-right">
                      <Link
                        to={`/app/students/${s.id}/edit`}
                        className="text-muted hover:text-accent text-[12px]"
                      >
                        Редактировать
                      </Link>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>

        {pages > 1 ? (
          <div className="mt-4 flex gap-1 justify-end text-[12px] text-muted">
            <button
              disabled={(q.page ?? 1) <= 1}
              onClick={() => updateSp({ page: String((q.page ?? 1) - 1) })}
              className="px-2 py-1 rounded hover:text-accent disabled:opacity-30"
            >
              <IconArrowLeft size={12} />
            </button>
            {Array.from({ length: pages }).map((_, i) => {
              const p = i + 1;
              const isCurrent = p === q.page;
              return (
                <button
                  key={p}
                  onClick={() => updateSp({ page: String(p) })}
                  className={
                    'min-w-[28px] px-2 py-1 rounded text-[12px] ' +
                    (isCurrent
                      ? 'bg-panel border border-border text-fg'
                      : 'hover:text-accent')
                  }
                >
                  {p}
                </button>
              );
            })}
            <button
              disabled={(q.page ?? 1) >= pages}
              onClick={() => updateSp({ page: String((q.page ?? 1) + 1) })}
              className="px-2 py-1 rounded hover:text-accent disabled:opacity-30"
            >
              <IconArrowRight size={12} />
            </button>
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}
